import { AppThunk } from 'store';
import { setHubBroadcastConnected } from './hubSlice';
import { updateEvent } from 'features/events/eventsSlice';
import {
  HubEventMessage,
  SocketMessage,
  HubListenerCount,
  HubActionCreated,
  HubBroadcastStart
} from 'features/hub/hub';
import { Broadcast, updateBroadcast } from 'features/broadcast/broadcastsSlice';
import { addHeartMessage } from 'features/chat/chatSlice';
import {
  broadcastSelector,
  fallbackBroadcastSelector
} from 'features/broadcast/selectors';
import { DateTime } from 'luxon';
import { setFallbackEnabled } from 'features/channel/channelSlice';

export const handleBroadcastFinished =
  (
    message: SocketMessage,
    disconnectBroadcast?: (_: string) => void,
    currentBroadcast?: Broadcast | null
  ): AppThunk =>
  async (dispatch) => {
    const uid = message.channel.split(';')[2];

    const broadcastUpdate = {
      uid: uid,
      isLive: false,
      finished: true
    };

    dispatch(updateBroadcast(broadcastUpdate));

    if (disconnectBroadcast) {
      disconnectBroadcast(uid);
    } else {
      dispatch(setHubBroadcastConnected(false));
    }

    // Don't restart the fallback if the broadcast that finished is the fallback
    // This means it's been toggle off by the creator
    if (currentBroadcast?.fallback && currentBroadcast?.uid == uid) {
      dispatch(setFallbackEnabled(false));
      return;
    }

    dispatch(startFallbackBroadcast());
  };

export const handleEventStopped =
  (message: HubEventMessage): AppThunk =>
  async (dispatch) => {
    dispatch(
      updateEvent({
        id: message.event.id.toString(),
        active: false,
        endedAt: DateTime.now().toISO(),
        lastHubEventType: null
      })
    );
  };

export const handleListenerCountMessage =
  (message: SocketMessage): AppThunk =>
  async (dispatch) => {
    const uid = message.channel.split(';')[2];

    const listenerMessage = message.data as HubListenerCount;

    const broadcastUpdate = {
      uid: uid,
      listenerCount: listenerMessage.count
    };

    dispatch(updateBroadcast(broadcastUpdate));
  };

export const handleActionCreated =
  (message: SocketMessage): AppThunk =>
  async (dispatch, getState) => {
    const actionCreatedMessage = message.data as HubActionCreated;

    const broadcast = broadcastSelector(
      getState(),
      actionCreatedMessage.broadcast_uid
    );

    if (!broadcast) {
      return;
    }

    const heartCount = Math.max(
      (broadcast.heartCount || 0) + 1,
      actionCreatedMessage.total
    );

    const broadcastUpdate = {
      uid: broadcast.uid,
      heartCount: heartCount
    };

    const heartMessage = {
      display_name: actionCreatedMessage.username,
      user_id: actionCreatedMessage.user_id,
      created_at: actionCreatedMessage.created_at
    };

    dispatch(updateBroadcast(broadcastUpdate));
    dispatch(addHeartMessage(heartMessage));
  };

export const startFallbackBroadcast =
  (): AppThunk => async (dispatch, getState) => {
    const fallBackbroadcast = fallbackBroadcastSelector(getState());

    if (!fallBackbroadcast) return;

    console.log('[hub] Restarting the fallback broadcast', fallBackbroadcast);

    dispatch(setFallbackEnabled(true));

    const broadcastUpdate = {
      uid: fallBackbroadcast.uid,
      isLive: true,
      finished: false
    };

    dispatch(updateBroadcast(broadcastUpdate));

    if (fallBackbroadcast.eventId) {
      dispatch(
        updateEvent({
          id: fallBackbroadcast.eventId,
          active: true,
          endedAt: null
        })
      );
    }
  };

export const forceFinishBroadcast =
  (
    currentBroadcast: Broadcast | null,
    hubBroadcast?: HubBroadcastStart,
    disconnectBroadcast?: (_: string) => void,
    isScheduledRecordingAudioEnded?: boolean
  ): AppThunk =>
  async (dispatch) => {
    if (!currentBroadcast) return;
    if (hubBroadcast?.broadcast.uid === currentBroadcast?.uid) return;
    if (currentBroadcast?.finished) return;
    if (currentBroadcast?.fallback || isScheduledRecordingAudioEnded) {
      console.log(
        '[hub] Forcing event:stopped / broadcast:finished on event',
        currentBroadcast
      );

      dispatch(
        handleBroadcastFinished(
          {
            channel: `${import.meta.env.VITE_HUB_ENV};broadcast;${
              currentBroadcast.uid
            }`,
            data: {},
            type: 'finished'
          },
          disconnectBroadcast
        )
      );

      if (currentBroadcast.eventId) {
        dispatch(
          updateEvent({
            id: currentBroadcast.eventId,
            active: false,
            endedAt: DateTime.now().toISO(),
            lastHubEventType: null
          })
        );
      }
    }
  };
